import AgencyHelper from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTBSButton, {
    KTBSButtonColor,
    KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';
import AppColors from 'general/constants/AppColors';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import { useEkyc } from 'hooks/useEKYC';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ShowMoreText from 'react-show-more-text';

const MaintainerUIHelper = {

    // MARK: ---- CARDS ----
    buildTableInfo: (title, content) => {
        const filteredContent = content?.filter((item) => !item.hide);

        const TableRow = ({ item, isLast }) => {
            const textRef = useRef(null);
            const [isOverflowing, setIsOverflowing] = useState(false);

            useEffect(() => {
                const element = textRef.current;
                if (element) {
                    setIsOverflowing(element.scrollWidth > element.clientWidth);
                }
            }, [item?.value]);

            const textStyles = {
                color: item?.color || AppColors.gray[800],
                fontWeight: 600,
                fontSize: Utils.returnFontSize(13),
                cursor: item?.action ? 'pointer' : 'default',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
            };

            const TextContent = (
                <div
                    ref={textRef}
                    className="font-weight-bolder text-right"
                    style={textStyles}
                    onClick={item?.action ?? (() => { })}
                >
                    <span>{item?.value || ''}</span>
                </div>
            );

            return (
                <>
                    {!item?.notShow && (
                        <div
                            className="d-flex justify-content-between p-3"
                            style={{
                                borderBottom: isLast ? 'none' : `1px dashed ${AppColors.gray[300]}`,
                            }}
                        >
                            <div
                                className="mr-3"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: 500,
                                    fontSize: Utils.returnFontSize(13),
                                }}
                            >
                                {item?.name}
                            </div>
                            <div
                                className="d-flex gap-2 align-items-center"
                                style={{
                                    cursor: item?.action ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                    flex: 1,
                                }}
                            >
                                <div style={{ overflow: 'hidden', flex: 1 }}>
                                    {isOverflowing ? (
                                        <KTTooltip offset={[0, 0]} text={item?.value}>
                                            {TextContent}
                                        </KTTooltip>
                                    ) : (
                                        TextContent
                                    )}
                                </div>
                                {item?.isCopy && (
                                    <i
                                        className="fa-regular fa-copy cursor-pointer"
                                        style={{
                                            width: 16,
                                            height: 18,
                                            color: AppColors.gray[800],
                                            flexShrink: 0,
                                        }}
                                        onClick={() =>
                                            Utils.copyTextToClipboard(item?.value, () =>
                                                ToastHelper.showSuccess('Đã sao chép')
                                            )
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {title}
                </div>
                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    {filteredContent?.map((item, index) =>
                        !item?.hide ? (
                            <TableRow key={index} item={item} isLast={index === filteredContent.length - 1} />
                        ) : null
                    )}
                </div>
            </div>
        );
    },

    buildAccountInfoCard: (maintainerDetail, isMaintainerIndividual) => {
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo(t('Thông tin cơ bản'), [
                    {
                        name: isMaintainerIndividual ? t('Họ và tên') : t('Tên tổ chức/doanh nghiệp'),
                        value: maintainerDetail?.name,
                        color: AppColors.gray[800],
                    },
                    {
                        name: t('Mã số thuế'),
                        value: maintainerDetail?.taxCode,
                        color: AppColors.gray[800],
                        hide: isMaintainerIndividual,
                    },
                    {
                        name: t('Số điện thoại'),
                        value: maintainerDetail?.phone,
                        color: AppColors.gray[800],
                    },
                    { name: t('Email'), value: maintainerDetail?.email, color: AppColors.gray[800] },
                    { name: t('Vai trò'), value: 'Kỹ thuật viên', color: AppColors.gray[800] },
                    {
                        name: t(`Loại kỹ thuật viên`),
                        value: t(`accountType_${maintainerDetail?.type}`),
                        color: AppColors.gray[800],
                    },
                ])}
            </div>
        );
    },

    buildEKYCInfoCard: (maintainerDetail, onDone) => {
        const { startEkyc } = useEkyc();
        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {t('Thông tin định danh eKYC')}
                </div>

                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    <div
                        className="d-flex justify-content-between p-3"
                        style={{
                            borderBottom: !maintainerDetail?.ekycVerified ? '' : `1px dashed ${AppColors.gray[300]}`,
                        }}
                    >
                        <div
                            className="font-weight-bold"
                            style={{
                                color: AppColors.gray[600],
                                fontWeight: '500',
                                fontSize: Utils.returnFontSize(13),
                            }}
                        >
                            {t('status')}
                        </div>
                        <div
                            className="font-weight-bolder"
                            style={{
                                color: AppColors.gray[800],
                                fontWeight: '600',
                                fontSize: Utils.returnFontSize(14),
                            }}
                            onClick={() => { }}
                        >
                            <KTLabel
                                text={maintainerDetail?.ekycVerified ? t(`Đã xác thực`) : t(`Chưa xác thực`)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-${maintainerDetail?.ekycVerified ? 'success' : 'danger'
                                    }`}
                            />
                        </div>
                    </div>
                    {maintainerDetail?.ekycVerified ? (
                        <div className="d-flex justify-content-between pt-3 mx-0 mb-3 px-3">
                            <div
                                className="w-120px font-weight-bold"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: '400',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                            >
                                {t('Dữ liệu xác thực')}
                            </div>
                            <div
                                className="font-weight-bolder cursor-pointer"
                                style={{
                                    color: AppColors.primary,
                                    fontWeight: '600',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                                onClick={() => {
                                    if (maintainerDetail?.idCardDocuments?.length)
                                        DealerHelper.handleShowFile(maintainerDetail?.idCardDocuments);
                                }}
                            >
                                {maintainerDetail?.idCardDocuments?.length
                                    ? `Xem tệp đính kèm (${maintainerDetail?.idCardDocuments?.length})`
                                    : ''}
                            </div>
                        </div>
                    ) : (
                        <div className="px-3 mb-3">
                            <KTBSButton
                                iconElement={
                                    <i className="fa-regular fa-shield-check pr-0" style={{ fontSize: 15 }} />
                                }
                                color={KTBSButtonColor.secondary}
                                title={t('Xác thực')}
                                size={KTBSButtonSize.large}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                buttonTitleFontSize={13}
                                onPress={() => {
                                    startEkyc(`Agency|${maintainerDetail?.agencyId}`, () => onDone());
                                }}
                                buttonTitleColor={AppColors.gray[800]}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    },

    buildDocumentInfoCard: (maintainerDetail, isMaintainerIndividual) => {
        const idCardDocuments = maintainerDetail?.ekycVerified === 1
            ? maintainerDetail?.idCardDocuments?.slice(1) ?? []
            : maintainerDetail?.idCardDocuments ?? []
        return (
            <div>
                {isMaintainerIndividual
                    ? MaintainerUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Loại giấy tờ định danh'),
                            value: isMaintainerIndividual
                                ? maintainerDetail?.idCardType === 'CC'
                                    ? 'CCCD'
                                    : 'Hộ chiếu'
                                : 'Giấy phép kinh doanh',
                        },
                        {
                            name: isMaintainerIndividual
                                ? maintainerDetail?.idCardType === 'CC'
                                    ? 'Số CCCD'
                                    : 'Số hộ chiếu'
                                : 'Số giấy phép kinh doanh',
                            value: maintainerDetail?.idCardNumber,
                            hide: !isMaintainerIndividual,
                        },
                        {
                            name: t('Ngày cấp'),
                            value: Utils.formatDateTimeToString(maintainerDetail?.idCardIssueDate, 'DD/MM/YYYY'),
                        },
                        {
                            name: t('Nơi cấp'),
                            value: maintainerDetail?.idCardPlaceOfIssue,
                            hide: maintainerDetail?.idCardType !== 'CC',
                        },
                        {
                            name: t('Hình ảnh giấy tờ'),
                            value: idCardDocuments?.length
                                ? `Xem tệp đính kèm (${idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (idCardDocuments?.length)
                                    DealerHelper.handleShowFile(idCardDocuments);
                            },
                        },
                    ])
                    : MaintainerUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Giấy phép ĐKKD'),
                            value: maintainerDetail?.idCardDocuments?.length
                                ? `Xem tệp đính kèm (${maintainerDetail?.idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (maintainerDetail?.idCardDocuments?.length)
                                    DealerHelper.handleShowFile(maintainerDetail?.idCardDocuments);
                            },
                        },
                    ])}
            </div>
        );
    },

    buildOtherInfoCard: (maintainerDetail, maintainerParentArea, maintainerArea, maintainerBank, isMaintainerIndividual, isAdminRole, isMaintainerInfo) => {
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo(t('Thông tin khác'), [
                    {
                        name: t('Người đại diện pháp luật'),
                        value: maintainerDetail?.legalRepresentative,
                        color: AppColors.gray[800],
                        hide: isMaintainerIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: maintainerDetail?.gender ? t(`gender_${maintainerDetail?.gender}`) : '',
                        hide: isMaintainerIndividual,
                    },
                    {
                        name: t('Chức vụ người đại diện'),
                        value: maintainerDetail?.legalPosition,
                        color: AppColors.gray[800],
                        hide: isMaintainerIndividual,
                    },
                    {
                        name: t('Quốc gia'),
                        value: AgencyHelper.Data.CountryOptions?.find(country => country?.value === maintainerDetail?.country)?.name,
                    },
                    {
                        name: t('Tỉnh/Thành phố'),
                        value: maintainerParentArea?.name,
                    },
                    { name: t('Phường/Xã'), value: maintainerArea?.name },
                    { name: t('Địa chỉ'), value: maintainerDetail?.address },
                    {
                        name: t('Ngày sinh'),
                        value: maintainerDetail?.dob ? Utils.formatDateTimeToString(maintainerDetail?.dob, 'DD/MM/YYYY') : '',
                        hide: !isMaintainerIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: maintainerDetail?.gender ? t(`gender_${maintainerDetail?.gender}`) : '',
                        hide: !isMaintainerIndividual,
                    },
                    {
                        name: t('Số tài khoản ngân hàng'),
                        value: maintainerBank?.bankAccount,
                        hide: isAdminRole || isMaintainerInfo,
                    },
                    {
                        name: t('Ngân hàng'),
                        value: maintainerBank?.bankName,
                        hide: isAdminRole || isMaintainerInfo,
                    },
                ])}
            </div>
        );
    },

    buildHsmAgreementCard: (maintainerDetail) => {
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo(t('Thông tin chữ ký số'), [
                    {
                        name:
                            <div className='d-flex gap-1 cursor-pointer'>
                                <span>Số thỏa thuận (Agreement UUID)</span>
                                <span>
                                    <KTTooltip
                                        text={`<div><span>Dùng để kích hoạt tính năng ký số ứng với hợp đồng và các văn bản điện tử.</span></div>`}
                                        textHeader="Số thỏa thuận (Agreement UUID)"
                                        isHtmlContent
                                    >
                                        <i
                                            className="fa-regular fa-info-circle"
                                            style={{ color: AppColors.info }}
                                        />
                                    </KTTooltip>
                                </span>
                            </div>,
                        value: maintainerDetail?.hsmAgreementUUID,
                    },
                ])}
            </div>
        );
    },

    buildWorkAddressCard: (maintainerDetail, maintainerParentArea, maintainerArea) => {
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo(t('Địa chỉ đăng ký làm việc'), [
                    {
                        name: t('Quốc gia'),
                        value: AgencyHelper.Data.CountryOptions?.find(country => country?.value === maintainerDetail?.country)?.name,
                    },
                    {
                        name: t('Tỉnh/Thành phố'),
                        value: maintainerParentArea?.name,
                    },
                    { name: t('Phường/Xã'), value: maintainerArea?.name },
                    { name: t('Địa chỉ'), value: maintainerDetail?.workAddress?.workAddress },
                    {
                        name: t('Tọa độ'),
                        value: `${maintainerDetail?.workAddress?.latitude}, ${maintainerDetail?.workAddress?.longitude}`,
                    },
                ])}
            </div>
        );
    },

    buildTicketInfoCard: (ticketDetail, currentLat, currentLon) => {
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo('Nội dung công việc', [
                    {
                        name: 'Độ ưu tiên',
                        value: (
                            <KTLabel
                                text={MaintainerHelper.Utils.getTicketPriorityName(ticketDetail?.priority)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-${MaintainerHelper.Utils.getTicketPriorityColor(ticketDetail?.priority)}`}
                            />
                        ),
                        color: AppColors.gray[800],
                    },
                    {
                        name: 'Loại công việc',
                        value: _.find(MaintainerHelper.Data.ticketTypeOptions, (item) => item?.value === ticketDetail?.type)?.name,
                        color: AppColors.gray[800],
                    },
                    {
                        name: 'Khoảng cách',
                        value: (() => {
                            const distance = Utils.calculateDistance(
                                currentLat,
                                currentLon,
                                ticketDetail?.chargeStation?.latitude,
                                ticketDetail?.chargeStation?.longitude
                            );
                            return Utils.formatNumber(distance) + ' km';
                        })(),
                        color: AppColors.gray[800],
                    },
                    {
                        name: 'Thời gian gửi lỗi',
                        value: Utils.formatDateTimeToString(ticketDetail?.createdAt, 'HH:mm - DD/MM/YYYY'),
                    },
                    {
                        name: 'Thời gian cần xử lý',
                        value: Utils.formatDateTimeToString(ticketDetail?.deadline, 'HH:mm - DD/MM/YYYY'),
                    },
                    {
                        name: 'Ảnh hiện trạng (Người báo lỗi)',
                        value: ticketDetail?.attachedFiles?.length
                            ? `Xem tệp đính kèm (${ticketDetail?.attachedFiles?.length})`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail?.attachedFiles?.length)
                                DealerHelper.handleShowFile(ticketDetail?.attachedFiles);
                        },
                    },
                ])}
            </div>
        );
    },

    buildTicketDescriptionCard: (title, description) => {
        return (
            <div>
                {UIHelper.buildSectionTitle(title)}
                {UIHelper.buildSectionCard(
                    <div
                        className="px-3 py-2"
                        style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}
                    >
                        <ShowMoreText
                            lines={3}
                            more="Xem thêm"
                            less="Thu gọn"
                            className=""
                            anchorClass="text-success font-size-normal font-weight-bolder cursor-pointer hover-opacity-60"
                            onClick={() => { }}
                            expanded={false}
                            truncatedEndingComponent={'... '}
                        >
                            {description}
                        </ShowMoreText>
                    </div>
                )}
            </div>
        )
    },

    buildTicketChargePointInfoCard: (ticketDetail) => {
        const areaTree = useSelector((state) => state.app.areaTree);
        const area = ChargeHelper.Utils.getAreaById([areaTree], ticketDetail?.chargeStation?.areaId);
        let parentArea;
        if (area?.typeCode === 'W') {
            parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
        }
        const addressResult = [ticketDetail?.chargeStation?.address, area?.name, parentArea?.name]
            .filter(Boolean)
            .join(', ');

        return (
            <div>
                {MaintainerUIHelper.buildTableInfo('Thông tin trụ sạc', [
                    {
                        name: 'Trạm sạc',
                        value: ticketDetail?.chargeStation?.name ?? '',
                        color: AppColors.primary,
                        action: () => {
                            Utils.openInNewTab(`${AppRoute.stationList}/${ticketDetail?.chargeStation?.stationId}`)
                        }
                    },
                    {
                        name: 'Trụ sạc bị lỗi',
                        value: ticketDetail?.chargePoint?.pointCode,
                        color: AppColors.primary,
                        action: () => {
                            Utils.openInNewTab(`${AppRoute.pointList}/${ticketDetail?.chargePoint?.pointId}`)
                        }
                    },
                    {
                        name: 'Địa chỉ',
                        value: addressResult?.length > 0 ? addressResult : '',
                        color: AppColors.gray[800],
                    },
                    {
                        name: 'Tên người quản lý trạm sạc',
                        value: ticketDetail?.chargeStation?.contactName ?? '',
                    },
                    {
                        name: 'Số điện thoại người quản lý',
                        value: ticketDetail?.chargeStation?.contactPhone ?? '',
                    },
                    {
                        name: 'Người báo lỗi',
                        value: ticketDetail?.createdBy?.fullname ?? '',
                    },
                ])}
            </div>
        );
    },

    buildTicketActionInfoCard: (ticketDetail) => {
        const handleViewFiles = (files) => {
            AppCustomDialogHelper.show(
                'Tệp đính kèm',
                <FileUploadView
                    pathList={files}
                    onCancel={() => {
                        AppCustomDialogHelper.hide();
                    }}
                />,
                'md',
                false,
                true
            );
        };
        return (
            <div>
                {MaintainerUIHelper.buildTableInfo('Thông tin sửa chữa/bảo trì', [
                    {
                        name: 'Thời gian KTV check-in',
                        value: Utils.formatDateTimeToString(ticketDetail?.checkinAt) ?? '',
                    },
                    {
                        name: 'Thời gian dự kiến hoàn thành',
                        value: Utils.formatDateTimeToString(ticketDetail?.estimateTime) ?? '',
                    },
                    {
                        name: 'Thời gian hoàn thành thực tế',
                        value: Utils.formatDateTimeToString(ticketDetail?.completedAt) ?? '',
                    },
                    {
                        name: 'Ảnh trước sửa chữa/bảo trì',
                        value: ticketDetail?.attachedFilesBefore?.length
                            ? `Xem tệp đính kèm (${ticketDetail?.attachedFilesBefore?.length})`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail?.attachedFilesBefore?.length)
                                DealerHelper.handleShowFile(ticketDetail?.attachedFilesBefore);
                        },
                    },
                    {
                        name: 'Ảnh sau sửa chữa/bảo trì',
                        value: ticketDetail?.attachedFilesAfter?.length
                            ? `Xem tệp đính kèm (${ticketDetail?.attachedFilesAfter?.length})`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail?.attachedFilesAfter?.length)
                                DealerHelper.handleShowFile(ticketDetail?.attachedFilesAfter);
                        },
                    },
                    {
                        name: <div className='d-flex gap-1 cursor-pointer'>
                            <span>Hóa đơn mua hàng</span>
                            <span>
                                <KTTooltip
                                    text={`<div><span>Chứng từ phát sinh khi KTV tự mua vật tư sửa chữa/thay thế trong các trường hợp: 
                                        Nhà điều hành không cung ứng kịp, kho không có sẵn hoặc việc mua ngoài đã được nhà điều hành phê duyệt.</span></div>`}
                                    textHeader="Hóa đơn mua hàng"
                                    isHtmlContent
                                >
                                    <i
                                        className="fa-regular fa-info-circle"
                                        style={{ color: AppColors.info }}
                                    />
                                </KTTooltip>
                            </span>
                        </div>,
                        value: ticketDetail?.invoiceDocument?.length
                            ? `Xem tệp đính kèm (${ticketDetail?.invoiceDocument?.length})`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail?.invoiceDocument?.length)
                                handleViewFiles(ticketDetail?.invoiceDocument);
                        },
                    },
                ])}
            </div>
        );
    },

    buildTicketAcceptanceInfoCard: (ticketDetail) => {
        const handleViewFiles = (files) => {
            AppCustomDialogHelper.show(
                'Tệp đính kèm',
                <FileUploadView
                    pathList={files}
                    onCancel={() => {
                        AppCustomDialogHelper.hide();
                    }}
                />,
                'md',
                false,
                true
            );
        };

        return (
            <div>
                {MaintainerUIHelper.buildTableInfo('Thông tin nghiệm thu, thanh toán', [
                    {
                        name: 'Biên bản nghiệm thu kỹ thuật',
                        value: ticketDetail.technicalAcceptanceDocument?.length
                            ? `Xem tệp đính kèm`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail.technicalAcceptanceDocument) {
                                Utils.openInNewTab(Utils.getFullUrl(ticketDetail.technicalAcceptanceDocument));
                            }
                        },
                    },
                    {
                        name: 'Biên bản nghiệm thu thanh toán',
                        value: ticketDetail.financialAcceptanceDocument?.length
                            ? `Xem tệp đính kèm`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail.financialAcceptanceDocument) {
                                Utils.openInNewTab(Utils.getFullUrl(ticketDetail.financialAcceptanceDocument));
                            }
                        },
                    },
                    {
                        name: 'Chi phí sửa chữa/bảo trì',
                        value: Utils.formatNumber(ticketDetail?.amount ?? 0) + ' VNĐ',
                        color: AppColors.gray[800],
                    },
                    {
                        name: 'Thuế VAT',
                        value: ticketDetail?.vat === 0 ? 'KCT' : ticketDetail?.vat * 100 + '%',
                    },
                    {
                        name: 'Thuế TNCN',
                        value: ticketDetail?.tax === 0 ? 'KCT' : ticketDetail?.tax * 100 + '%',
                    },
                    {
                        name: 'Tổng chi phí',
                        value: Utils.formatNumber(
                            _.sum([
                                ticketDetail?.amount ?? 0,
                                (ticketDetail?.amount ?? 0) * (ticketDetail?.vat ?? 0),
                                (ticketDetail?.amount ?? 0) * (ticketDetail?.tax ?? 0),
                            ])
                        ) + ' VNĐ',
                    },
                    {
                        name: 'Trạng thái thanh toán',
                        value: (
                            <KTLabel
                                text={MaintainerHelper.Utils.getPaymentStatusName(ticketDetail?.paymentStatus)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-${MaintainerHelper.Utils.getPaymentStatusColor(ticketDetail?.paymentStatus)}`}
                            />
                        ),
                    },
                    {
                        name: 'Chứng từ thanh toán',
                        value: ticketDetail?.paymentDocument?.length
                            ? `Xem tệp đính kèm (${ticketDetail?.paymentDocument?.length})`
                            : '',
                        color: AppColors.primary,
                        action: () => {
                            if (ticketDetail?.paymentDocument?.length)
                                handleViewFiles(ticketDetail?.paymentDocument);
                        },
                    },
                ])}
            </div>
        );
    },

    // MARK: ---- DIALOGS ----
    buildShowFileDialog: (fileList, onClosed = () => { }) => {
        return (
            <div
                className="rounded-bottom-lg p-5"
                style={{
                    backgroundColor: AppColors.gray[100],
                }}
            >
                <div
                    className="bg-white rounded"
                    style={{
                        border: `1px solid ${AppColors.gray[300]}`,
                    }}
                >
                    {fileList?.map((document, index) => (
                        <div
                            key={index}
                            className="font-weight-boldest p-3 cursor-pointer"
                            style={{
                                color: AppColors.primary,
                                borderBottom:
                                    index !== fileList?.length - 1 ? `1px dashed ${AppColors.gray[300]}` : '',
                            }}
                            onClick={() => {
                                DealerHelper.handleShowImage(fileList[index], Utils.getFullUrl(document));
                            }}
                        >
                            {fileList[index]}
                        </div>
                    ))}
                </div>
            </div>
        );
    },


    // MARK: ---- BUTTONS ----
    buildToggleSwitch: (isOn, handleToggle) => {
        return (
            <div
                className="position-relative d-inline-block"
                style={{
                    width: 43,
                    height: 26,
                }}
                onClick={() => handleToggle()}
            >
                <div
                    className="rounded-pill"
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: isOn ? AppColors.primary : AppColors.gray[400],
                        transition: 'background-color 0.3s ease',
                        cursor: 'pointer',
                    }}
                />
                <div
                    className="position-absolute rounded-circle"
                    style={{
                        width: 22,
                        height: 22,
                        backgroundColor: 'white',
                        top: 2,
                        left: isOn ? 19 : 2,
                        transition: 'left 0.3s ease',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    }}
                />
            </div>
        );
    },
};

export default MaintainerUIHelper;
